import { createTheme, alpha } from '@mui/material/styles';

/**
 * Application theme configuration for Material-UI
 * Modern design system with enhanced colors, shadows, and components
 */

// Custom color palette
const colors = {
  primary: {
    main: '#1e3a8a',
    light: '#3b5cb8',
    dark: '#152a66',
    gradient: 'linear-gradient(135deg, #1e3a8a 0%, #3b5cb8 100%)',
  },
  secondary: {
    main: '#3b82f6',
    light: '#60a5fa',
    dark: '#2563eb',
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
  },
  accent: {
    coral: '#f97316',
    teal: '#14b8a6',
    amber: '#f59e0b',
    rose: '#f43f5e',
  },
  background: {
    default: '#f8fafc',
    paper: '#ffffff',
    subtle: '#f1f5f9',
  },
};

// Modern soft shadow system
const shadows = {
  soft: {
    sm: '0 2px 8px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.06)',
    md: '0 4px 16px rgba(0, 0, 0, 0.06), 0 2px 4px rgba(0, 0, 0, 0.04)',
    lg: '0 10px 40px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.04)',
    xl: '0 20px 60px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.06)',
  },
  colored: {
    primary: '0 8px 24px rgba(30, 58, 138, 0.25)',
    secondary: '0 8px 24px rgba(59, 130, 246, 0.25)',
  },
};

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colors.primary.main,
      light: colors.primary.light,
      dark: colors.primary.dark,
    },
    secondary: {
      main: colors.secondary.main,
      light: colors.secondary.light,
      dark: colors.secondary.dark,
    },
    text: {
      primary: '#0f172a',
      secondary: '#64748b',
    },
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    divider: 'rgba(0, 0, 0, 0.06)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 16,
    h1: {
      fontWeight: 800,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 700,
      letterSpacing: '-0.015em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.925rem',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colors.background.default,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: shadows.soft.md,
          border: '1px solid rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: shadows.soft.lg,
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        },
        contained: {
          boxShadow: shadows.soft.sm,
          color: '#ffffff',
          '&:hover': {
            boxShadow: shadows.colored.primary,
            transform: 'translateY(-1px)',
            color: '#ffffff',
          },
          '&:active': {
            transform: 'translateY(0)',
          },
          '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.7)',
          },
        },
        containedPrimary: {
          background: colors.primary.gradient,
          color: '#ffffff !important',
          '&:hover': {
            background: colors.primary.gradient,
            boxShadow: shadows.colored.primary,
            color: '#ffffff !important',
          },
          '&.Mui-disabled': {
            background: 'rgba(30, 58, 138, 0.5)',
            color: 'rgba(255, 255, 255, 0.7) !important',
          },
        },
        containedSecondary: {
          background: colors.secondary.gradient,
          color: '#ffffff !important',
          '&:hover': {
            background: colors.secondary.gradient,
            boxShadow: shadows.colored.secondary,
            color: '#ffffff !important',
          },
          '&.Mui-disabled': {
            background: 'rgba(59, 130, 246, 0.5)',
            color: 'rgba(255, 255, 255, 0.7) !important',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
            backgroundColor: alpha(colors.primary.main, 0.04),
          },
        },
        text: {
          '&:hover': {
            backgroundColor: alpha(colors.primary.main, 0.06),
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.05)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 500,
          transition: 'all 0.2s ease',
        },
        filled: {
          '&:hover': {
            transform: 'scale(1.02)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            transition: 'all 0.2s ease',
            '&:hover': {
              boxShadow: shadows.soft.sm,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.12)}`,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: shadows.soft.xl,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
          fontWeight: 600,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.95rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderRadius: '3px 3px 0 0',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
        standardSuccess: {
          backgroundColor: alpha('#10b981', 0.1),
          color: '#047857',
        },
        standardWarning: {
          backgroundColor: alpha('#f59e0b', 0.1),
          color: '#b45309',
        },
        standardError: {
          backgroundColor: alpha('#ef4444', 0.1),
          color: '#dc2626',
        },
        standardInfo: {
          backgroundColor: alpha('#3b82f6', 0.1),
          color: '#2563eb',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: {
          borderRadius: 16,
        },
        elevation1: {
          boxShadow: shadows.soft.sm,
        },
        elevation2: {
          boxShadow: shadows.soft.md,
        },
        elevation3: {
          boxShadow: shadows.soft.lg,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.8rem',
          backgroundColor: '#1e293b',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          boxShadow: shadows.soft.lg,
          border: '1px solid rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          transition: 'all 0.15s ease',
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: shadows.soft.lg,
          '&:hover': {
            boxShadow: shadows.colored.primary,
          },
        },
        primary: {
          background: colors.primary.gradient,
          '&:hover': {
            background: colors.primary.gradient,
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          backgroundColor: alpha(colors.primary.main, 0.1),
        },
        bar: {
          borderRadius: 4,
          background: colors.primary.gradient,
        },
      },
    },
  },
});

// Export custom values for use in components
export const customStyles = {
  colors,
  shadows,
  gradients: {
    primary: colors.primary.gradient,
    secondary: colors.secondary.gradient,
    hero: 'linear-gradient(135deg, #1e3a8a 0%, #3b5cb8 50%, #60a5fa 100%)',
    card: 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(0,0,0,0.6) 100%)',
    glass: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
  },
  glass: {
    background: 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px) saturate(180%)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
  },
};
